'use client'

import { useState, useEffect } from 'react'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Save, ChevronDown } from 'lucide-react'
import type { SceneInput } from '@creanizer/types'
import { useCustomValuesStore } from '../../../../stores/customValuesStore'

interface SceneFormSettingsProps {
  input: SceneInput
  onChange: (input: SceneInput) => void
  useStreaming: boolean
  onStreamingChange: (value: boolean) => void
}

export function SceneFormSettings({ 
  input, 
  onChange, 
  useStreaming, 
  onStreamingChange 
}: SceneFormSettingsProps) {
  const [showCustomStyles, setShowCustomStyles] = useState(false)
  const [customStyleName, setCustomStyleName] = useState('')
  const [avoidElementsText, setAvoidElementsText] = useState(input.avoidElements?.join('、') || '')
  
  const { 
    addCustomValue, 
    getCustomValues, 
    useCustomValue,
    init 
  } = useCustomValuesStore()
  
  // カスタム値ストアの初期化
  useEffect(() => {
    init()
  }, [])
  
  // 外部からのinput変更時に内部stateを同期
  useEffect(() => {
    setAvoidElementsText(input.avoidElements?.join('、') || '')
  }, [input.avoidElements])
  
  // カスタム文体を保存
  const saveCustomStyle = async () => {
    if (input.customStyle && customStyleName) {
      try {
        await addCustomValue('writingStyle', input.customStyle, customStyleName, 'scene')
        alert(`カスタム文体「${customStyleName}」を保存しました`)
        setCustomStyleName('')
      } catch (error) {
        console.error('Failed to save custom style:', error)
        alert('保存に失敗しました')
      }
    }
  }
  
  // 保存済みカスタム文体を選択
  const selectCustomStyle = async (value: any) => {
    await useCustomValue(value.id)
    onChange({ ...input, writingStyle: 'custom', customStyle: value.value })
  }
  return (
    <details className="mt-4 border rounded-lg p-4">
      <summary className="cursor-pointer font-semibold">詳細設定</summary>
      <div className="space-y-4 pt-4">
            {/* 文章設定 */}
            <div className="grid grid-cols-3 gap-4">
              {/* 視点 */}
              <div>
                <Label htmlFor="perspective">視点</Label>
                <Select
                  value={input.perspective || 'third_limited'}
                  onValueChange={(value) => onChange({ ...input, perspective: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first">一人称</SelectItem>
                    <SelectItem value="third_limited">三人称限定視点</SelectItem>
                    <SelectItem value="third_omniscient">三人称神視点</SelectItem>
                    <SelectItem value="second">二人称</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* 文体 */}
              <div>
                <Label htmlFor="writingStyle">文体</Label>
                <Select
                  value={input.writingStyle || 'novel'}
                  onValueChange={(value) => onChange({ ...input, writingStyle: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="novel">小説風</SelectItem>
                    <SelectItem value="light_novel">ライトノベル風</SelectItem>
                    <SelectItem value="literary">純文学風</SelectItem>
                    <SelectItem value="hardboiled">ハードボイルド</SelectItem>
                    <SelectItem value="lovecraft">ラヴクラフト風</SelectItem>
                    <SelectItem value="mystery">ミステリー風</SelectItem>
                    <SelectItem value="romance">ロマンス風</SelectItem>
                    <SelectItem value="web_novel">ウェブ小説風</SelectItem>
                    <SelectItem value="fairy_tale">童話風</SelectItem>
                    <SelectItem value="screenplay">脚本風</SelectItem>
                    <SelectItem value="custom">カスタム</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* カスタム文体入力フィールド */}
            {input.writingStyle === 'custom' && (
              <div className="space-y-2">
                <Label htmlFor="customStyle">カスタム文体の詳細</Label>
                <Textarea
                  id="customStyle"
                  value={input.customStyle || ''}
                  onChange={(e) => onChange({ ...input, customStyle: e.target.value })}
                  placeholder="文体の特徴を具体的に記述してください。例：「一人称で淡々とした語り口。現在形を基調とし、比喩や暗喩を多用。会話は簡潔だが含みを持たせる。」"
                  className="min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  10〜2000文字で、望む文体の特徴を詳しく説明してください。
                  語り口、時制、視点、比喩の使い方、文の長さ、リズムなどを具体的に記述すると、より期待に近い結果が得られます。
                </p>
                
                {/* カスタム文体の保存 */}
                <div className="flex gap-2 mt-2">
                  <Input
                    value={customStyleName}
                    onChange={(e) => setCustomStyleName(e.target.value)}
                    placeholder="保存する文体の名前（例：村上春樹風）"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={saveCustomStyle}
                    disabled={!input.customStyle || !customStyleName}
                  >
                    <Save className="h-4 w-4 mr-1" />
                    保存
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setShowCustomStyles(!showCustomStyles)}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* 保存済みカスタム文体リスト */}
                {showCustomStyles && (
                  <div className="border rounded p-2 max-h-48 overflow-y-auto">
                    {getCustomValues('writingStyle', 'scene').length === 0 ? (
                      <p className="text-sm text-gray-500">保存されたカスタム文体はありません</p>
                    ) : (
                      <div className="space-y-1">
                        {getCustomValues('writingStyle', 'scene').map((value) => (
                          <button
                            key={value.id}
                            type="button"
                            className="block w-full text-left p-2 hover:bg-gray-100 rounded text-sm"
                            onClick={() => selectCustomStyle(value)}
                          >
                            <div className="font-medium">{value.label}</div>
                            <div className="text-xs text-muted-foreground line-clamp-2">
                              {value.value}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 創造性レベル */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>創造性レベル</Label>
                <span className="text-sm text-muted-foreground">{input.creativityLevel || 7}</span>
              </div>
              <Slider
                value={[input.creativityLevel || 7]}
                onValueChange={([value]) => onChange({ ...input, creativityLevel: value })}
                min={1}
                max={10}
                step={1}
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>保守的</span>
                <span>標準</span>
                <span>創造的</span>
              </div>
            </div>

            {/* 禁止ワード */}
            <div>
              <Label htmlFor="avoidElements">禁止ワード・表現（オプション）</Label>
              <Textarea
                id="avoidElements"
                value={avoidElementsText}
                onChange={(e) => setAvoidElementsText(e.target.value)}
                onBlur={() => {
                  // フォーカスが外れた時に配列に変換
                  const words = avoidElementsText
                    .split(/[、,，\n]/)  // 全角読点、半角カンマ、全角カンマ、改行に対応
                    .map(w => w.trim())
                    .filter(w => w.length > 0)
                  onChange({ ...input, avoidElements: words })
                }}
                placeholder="避けたい単語や表現をカンマか読点で区切って入力（例：血、暴力、死）"
                className="min-h-[60px]"
              />
              <p className="text-xs text-muted-foreground mt-1">
                生成されるシーンで使用を避けたい単語や表現を指定できます。
                複数指定する場合は読点（、）またはカンマ（,）で区切ってください。
              </p>
            </div>

            {/* Proモード（設計書通りCheckbox使用） */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useProMode"
                checked={input.useProMode || false}
                onCheckedChange={(checked) => 
                  onChange({ 
                    ...input, 
                    useProMode: checked as boolean,
                    useProLiteMode: checked ? false : input.useProLiteMode // Proモードがオンの時はProライトをオフ
                  })
                }
              />
              <Label htmlFor="useProMode" className="cursor-pointer">
                Proモード（より詳細で高品質な生成）
              </Label>
            </div>

            {/* Proモードライト（実験的） */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useProLiteMode"
                checked={input.useProLiteMode || false}
                onCheckedChange={(checked) =>
                  onChange({
                    ...input,
                    useProLiteMode: checked as boolean,
                    useProMode: checked ? false : input.useProMode // Proライトがオンの時はProモードをオフ
                  })
                }
              />
              <Label htmlFor="useProLiteMode" className="cursor-pointer flex items-center space-x-2">
                <span>Proモードライト（実験的）</span>
                <span className="text-xs text-muted-foreground">[thinking_budget: 1024]</span>
              </Label>
            </div>

            {/* Gemini 3モード */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useGemini3Mode"
                checked={input.useGemini3Mode || false}
                onCheckedChange={(checked) =>
                  onChange({
                    ...input,
                    useGemini3Mode: checked as boolean,
                    useGemini3FlashMode: checked ? false : input.useGemini3FlashMode
                  })
                }
              />
              <Label htmlFor="useGemini3Mode" className="cursor-pointer">
                Gemini 3 モード（有料APIのみ・試験版）
              </Label>
            </div>

            {/* Gemini 3 Flashモード */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useGemini3FlashMode"
                checked={input.useGemini3FlashMode || false}
                onCheckedChange={(checked) =>
                  onChange({
                    ...input,
                    useGemini3FlashMode: checked as boolean,
                    useGemini3Mode: checked ? false : input.useGemini3Mode
                  })
                }
              />
              <Label htmlFor="useGemini3FlashMode" className="cursor-pointer">
                Gemini 3 Flash モード（有料APIのみ・試験版・高速）
              </Label>
            </div>

            {/* ストリーミングモード（設計書通りCheckbox使用） */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useStreaming"
                checked={useStreaming}
                onCheckedChange={(checked) => onStreamingChange(checked as boolean)}
              />
              <Label htmlFor="useStreaming" className="cursor-pointer">
                ストリーミングモード（生成過程をリアルタイム表示）
              </Label>
            </div>

            {/* 説明テキスト */}
            <div className="text-sm text-muted-foreground space-y-2 pt-2 border-t">
              <p>
                <strong>視点について：</strong>
              </p>
              <ul className="ml-4 list-disc">
                <li>一人称：「私は〜」主人公の内面を深く描写</li>
                <li>三人称限定：特定キャラの視点から描写</li>
                <li>三人称神視点：全知の語り手として描写</li>
                <li>二人称：「あなたは〜」読者に語りかける形式</li>
              </ul>
              
              <p className="pt-2">
                <strong>文体について：</strong>
              </p>
              <ul className="ml-4 list-disc">
                <li>小説風：一般的な小説の文体</li>
                <li>ライトノベル風：軽快で読みやすい文体</li>
                <li>純文学風：文学的で深みのある文体</li>
                <li>ハードボイルド：簡潔で硬質な文体</li>
                <li>ラヴクラフト風：幻想的で重厚な文体</li>
                <li>ミステリー風：論理的で緊張感のある文体</li>
                <li>ロマンス風：情感豊かで繊細な文体</li>
                <li>ウェブ小説風：現代的でテンポの良い文体</li>
                <li>童話風：優しく親しみやすい文体</li>
                <li>脚本風：台詞とト書きの形式</li>
              </ul>
            </div>
      </div>
    </details>
  )
}